import React, { useState, useEffect, useMemo } from "react";
import { authFetch } from "../../utils/api";
import "../../styles/admin/admin-dashboard.css";

// ... (Tus componentes de Iconos y StatCard se quedan igual, los omito para ahorrar espacio) ...
// --- Iconos ---
const IconBullhorn = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path d="M2 10v4a2 2 0 0 0 2 2h2v3a1 1 0 0 0 1.6.8L12 17h4a6 6 0 0 0 6-6V6a1 1 0 0 0-1.4-.9L14 8H6a4 4 0 0 0-4 4Z" fill="currentColor"/>
  </svg>
);
const IconInbox = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path d="M4 4h16l-2 12H6L4 4Zm2 14h12v2H6v-2Zm2-6h3l1 2h2l1-2h3" fill="currentColor"/>
  </svg>
);

const StatCard = ({label, value, icon}) => (
  <section className="card stat-card" aria-label={label}>
    <div className="stat-header">
      <p className="muted">{label}</p>
      <div className="stat-icon">{icon}</div>
    </div>
    <div className="stat-value">{value}</div>
  </section>
);

const calcMax = (arr) => Math.max(...arr, 1);

const BarChart = ({title, subtitle, data, labels, highlightIndex}) => {
  const max = calcMax(data);
  const total = data.reduce((a,b)=>a+b,0);
  
  return (
    <section className="card chart-card" role="figure" aria-label={title}>
      <header className="chart-head">
        <div>
          <h3>{title}</h3>
          <span className="muted small">{subtitle}</span>
        </div>
        <strong className="chart-total">{total}</strong>
      </header>

      <div className="chart-bars" role="img" aria-label={`${title} barras`}>
        {data.map((v, i) => {
          const h = (v / max) * 100;
          const active = i === highlightIndex;
          return (
            <div className="bar" key={i}>
              <div
                className={`bar-fill ${active ? "bar-highlight" : ""}`}
                style={{ height: `${h}%` }}
                title={`${labels[i]}: ${v}`}
              />
              <span className={`bar-label ${active ? "bar-label-active" : ""}`}>
                {labels[i]}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// --- Lógica de Procesamiento de Fechas ---
const getLast6MonthsLabels = () => {
  const labels = [];
  const date = new Date();
  date.setDate(1); 
  date.setMonth(date.getMonth() - 5);

  const monthNames = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

  for (let i = 0; i < 6; i++) {
    labels.push(monthNames[date.getMonth()]);
    date.setMonth(date.getMonth() + 1);
  }
  return labels;
};

// MODIFICACIÓN CLAVE: Ahora acepta 'dateKey' para saber qué fecha leer
const processMonthlyData = (items, dateKey, filterFn = () => true) => {
  const counts = [0, 0, 0, 0, 0, 0];
  const now = new Date();
  
  items.forEach(item => {
    // 1. Validar que el item, la fecha y el filtro existan
    if (!item[dateKey] || !filterFn(item)) return;

    // 2. Crear fecha basada en la clave dinámica (ej: 'fecha' o 'fechaAprobacion')
    const itemDate = new Date(item[dateKey]);
    
    // Validar que sea una fecha válida
    if (isNaN(itemDate.getTime())) return;

    // 3. Calcular diferencia en meses
    const diffMonths = (now.getFullYear() - itemDate.getFullYear()) * 12 + (now.getMonth() - itemDate.getMonth());

    if (diffMonths >= 0 && diffMonths < 6) {
      const index = 5 - diffMonths;
      counts[index]++;
    }
  });

  return counts;
};

/* ===== Pantalla Principal ===== */
export default function Dashboard() {
  const [stats, setStats] = useState({
    totalSolicitudes: 0,
    totalEmpresas: 0,
    pendientes: 0
  });
  
  const [chartData, setChartData] = useState({
    solicitudes: [0,0,0,0,0,0],
    empresasNuevas: [0,0,0,0,0,0]
  });

  const [loading, setLoading] = useState(true);
  const monthLabels = useMemo(() => getLast6MonthsLabels(), []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [resSolicitudes, resEmpresas] = await Promise.all([
          authFetch('/api/solicitudes'),
          authFetch('/api/lugar') 
        ]);

        let solicitudes = [];
        let empresasCount = 0;

        if (resSolicitudes.ok) {
          solicitudes = await resSolicitudes.json();
        }
        
        if (resEmpresas.ok) {
          const empresas = await resEmpresas.json();
          empresasCount = Array.isArray(empresas) ? empresas.length : 0;
        }

        // --- Estadísticas Generales ---
        const totalSolicitudes = solicitudes.length;
        const pendientes = solicitudes.filter(s => s.estadoDeAprobacion === 'Pendiente').length;

        setStats({
          totalSolicitudes,
          totalEmpresas: empresasCount,
          pendientes
        });

        // --- Procesamiento de Gráficas ---
        
        // GRÁFICA 1: Solicitudes Recibidas
        // Usa la fecha de creación (campo 'fecha')
        const dataSolicitudes = processMonthlyData(
          solicitudes, 
          'fecha' 
        );

        // GRÁFICA 2: Empresas Nuevas (Aprobadas)
        // Usa la fecha de aprobación. 
        // NOTA: Cambia 'fechaAprobacion' por el nombre real que venga de tu API (ej: 'updatedAt', 'fechaResolucion').
        // Si no tienes ese campo, dejará de contar correctamente el MES de aprobación.
        const dataAprobadas = processMonthlyData(
          solicitudes, 
          'fechaAprobacion', // <--- CAMBIA ESTO por el campo real de tu BD
          (s) => s.estadoDeAprobacion === 'Aprobada'
        );

        setChartData({
          solicitudes: dataSolicitudes,
          empresasNuevas: dataAprobadas
        });

      } catch (error) {
        console.error("Error cargando dashboard:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="admin-scope layout">
      <div className="content">
        <main className="main">
          <h1>Dashboard</h1>

          <div className="grid grid-2">
            <StatCard 
              label="Total Solicitudes (Pendientes)" 
              value={loading ? "..." : `${stats.totalSolicitudes} (${stats.pendientes})`} 
              icon={<IconBullhorn className="ic-24" />} 
            />
            <StatCard 
              label="Empresas Registradas Totales" 
              value={loading ? "..." : stats.totalEmpresas} 
              icon={<IconInbox className="ic-24" />} 
            />
          </div>

          <div className="grid grid-2">
            <BarChart 
              title="Solicitudes Recibidas" 
              subtitle="Últimos 6 meses" 
              data={chartData.solicitudes} 
              labels={monthLabels}
              highlightIndex={5} 
            />
            <BarChart 
              title="Nuevas Empresas (Aprobadas)" 
              subtitle="Últimos 6 meses" 
              data={chartData.empresasNuevas} 
              labels={monthLabels}
              highlightIndex={5} 
            />
          </div>
        </main>
      </div>
    </div>
  );
}